import React from 'react';
import styled from '@emotion/native';
import { green, accent, sand } from '@/shared/styles/colors';
import { BaseText, H3 } from '@/shared/components/StyledText';
import Button from '@/shared/components/Button';
import { Image } from 'react-native';
import { useRouter } from 'expo-router';
import { ROUTES } from '@/shared/constants/routes';
import OnboardingHeader from '@/shared/components/navigation/OnboardingHeader';
import { useApollo } from '@/services/apollo/ApolloProvider';
import useBreakpoint from '@/shared/hooks/useBreakpoint';
import { IS_WEB } from '@/shared/constants/utility';
import { IsDesktopProps } from '@/shared/styles/types';

const checklist = [
  'Join a vetted network of top-tier stylists',
  'Go live to get booked instantly or set your own hours',
  'Offer one-on-one video sessions—no texts, no drama',
  'Build your profile, grow your client base, and get paid for your expertise',
];

const clientChecklist = [
  'Match with professional stylists who understand your aesthetic',
  'Connect instantly or book a session that fits your schedule',
  "Get advice that's tailored, not templated",
  'Shop smarter with curated picks that fill the gaps in your wardrobe',
];

const WelcomeIntro = ({ welcomeSlug }: { welcomeSlug: string }) => {
  const router = useRouter();
  const { logOut } = useApollo();
  const { isDesktop } = useBreakpoint();

  const isStylist = welcomeSlug === 'stylist';
  const nextRoute = isStylist
    ? ROUTES.ONBOARDING.path
    : ROUTES.CLIENT_ONBOARDING.path;

  const handleContinue = () => {
    router.replace(nextRoute);
  };

  return (
    <ScreenContainer>
      <OnboardingHeader onPress={logOut} authText="Log Out" hideBackButton />
      <ContentContainer isDesktop={isDesktop}>
        <HeaderContainer>
          <Title>welcome to emme</Title>
          <Subtitle>Expert Stylists, On Demand</Subtitle>
        </HeaderContainer>
        <ChecklistCard>
          {(isStylist ? checklist : clientChecklist).map((item) => (
            <ChecklistRow key={item}>
              <CheckmarkIcon
                source={require('@/assets/images/CheckMarkWhite.png')}
                resizeMode="contain"
              />
              <ChecklistText>{item}</ChecklistText>
            </ChecklistRow>
          ))}
        </ChecklistCard>
      </ContentContainer>
      <ContinueButtonContainer isDesktop={isDesktop}>
        <Button
          text="continue"
          variant="secondary"
          onPress={handleContinue}
          customStyle={{
            width: 308,
          }}
        />
      </ContinueButtonContainer>
    </ScreenContainer>
  );
};

const ScreenContainer = styled.View({
  flex: 1,
  backgroundColor: green,
});

const Title = styled(H3)({
  textAlign: 'center',
  marginBottom: 8,
  color: sand,
  textTransform: 'uppercase',
});

const Subtitle = styled(BaseText)({
  textAlign: 'center',
  color: sand,
  fontSize: 18,
});

const ContentContainer = styled.View<{ isDesktop: boolean }>(
  ({ isDesktop }) => ({
    flex: 1,
    width: '100%',
    paddingHorizontal: isDesktop ? 48 : 0,
    alignItems: 'center',
    justifyContent: 'center',
  }),
);

const HeaderContainer = styled.View({
  marginBottom: 32,
});

const ChecklistCard = styled.View({
  paddingLeft: 29,
  paddingRight: 29,
  width: '100%',
  maxWidth: 509,
  paddingTop: 32,
});

const ChecklistRow = styled.View({
  flexDirection: 'row',
  alignItems: 'center',
  marginBottom: 41,
});

const CheckmarkIcon = styled(Image)({
  width: 27,
  height: 27,
  marginRight: 18,
  tintColor: green,
  backgroundColor: accent,
  borderRadius: 100,
  padding: 2,
});

const ChecklistText = styled(BaseText)({
  color: sand,
  fontSize: 17,
  flex: 1,
});

const ContinueButtonContainer = styled.View<IsDesktopProps>(
  ({ isDesktop }) => ({
    alignItems: isDesktop ? 'flex-end' : 'center',
    marginBottom: IS_WEB ? 60 : 0,
    paddingRight: isDesktop ? 48 : 0,
  }),
);

export default WelcomeIntro;
